import { CoachOnboardingBriefing } from '../../types/opsBriefing';
import { OnboardingSummary, summaryLine } from './coachOnboarding';

/**
 * 신규·과도기 히어로 — 시안 `.hero` 를 옮긴 것.
 *
 * ─────────────────────────────────────────────────────────────
 * 🔴 여기 숫자는 **아래 카드들과 반드시 맞아야 한다** (정책 §2)
 * ─────────────────────────────────────────────────────────────
 * 「오늘 챙길 일 4건」이라고 적어 놓고 카드가 다섯 장이면, 원장님은 그 순간
 * 화면 전체를 못 믿는다. 그래서 이 컴포넌트는 **아무것도 세지 않는다** —
 * 화면이 이미 세운 카드에서 나온 수를 그대로 받아 그린다.
 *
 * ⚠️ 「신규 / 과도기 / 정착」이라는 말은 **원장 화면에 나가지 않는다**
 *    (기존 결정 ④ · 정책 §1). 내부 라우팅 키일 뿐이라, 화면에는
 *    「센터 세팅 중」 같은 상태 서술만 쓴다.
 */

const NAVY = '#1E2A47';
const DIM = '#B8C4DA';
const FAINT = '#8FA0BE';

type Props = {
    dateLabel: string;
    /** 오늘 챙길 일 — **순항 카드는 빠진 수** */
    choreCount: number;
    setupSteps: CoachOnboardingBriefing['setupSteps'];
    blockedAt: string | null;
    membersAnalyzing: CoachOnboardingBriefing['membersAnalyzing'];
    summary: OnboardingSummary;
};

const OnboardingHero = ({
    dateLabel,
    choreCount,
    setupSteps,
    blockedAt,
    membersAnalyzing,
    summary,
}: Props) => (
    <div className="w-full rounded-[16px]" style={{ background: NAVY, color: '#FFFFFF', padding: '26px 32px 0' }}>
        <div className="flex flex-wrap items-start justify-between" style={{ gap: 24 }}>
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: DIM }}>{dateLabel}</div>
                <h1
                    style={{
                        fontSize: 30,
                        fontWeight: 800,
                        letterSpacing: '-0.6px',
                        marginTop: 8,
                        wordBreak: 'keep-all',
                    }}
                >
                    {choreCount > 0 ? (
                        <>
                            오늘 챙길 일 <span style={{ color: '#FF6B72' }}>{choreCount}건</span>
                        </>
                    ) : (
                        /* 0건이 되면 독촉 화면이 아니라 순항 화면이 된다 (정책 §2) */
                        <>오늘은 세팅이 순항 중입니다</>
                    )}
                    <small style={{ fontSize: 15, fontWeight: 700, color: FAINT, marginLeft: 8 }}>
                        · 센터 세팅 중
                    </small>
                </h1>
            </div>

            <div className="flex flex-wrap">
                <div style={{ padding: '0 28px 0 0' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: DIM }}>🧩 센터 세팅</div>
                    <div
                        className="flex items-baseline"
                        style={{ gap: 4, fontSize: 26, fontWeight: 800, color: '#FF8A90', marginTop: 4 }}
                    >
                        {setupSteps.done}
                        <span style={{ fontSize: 14, color: FAINT, fontWeight: 700 }}>
                            / {setupSteps.total}단계
                        </span>
                    </div>
                    {blockedAt && (
                        <div style={{ fontSize: 11.5, color: FAINT, marginTop: 3, wordBreak: 'keep-all' }}>
                            막힌 곳 · {blockedAt}
                        </div>
                    )}
                </div>
                <div style={{ padding: '0 0 0 28px', borderLeft: '1px solid #33415F' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: DIM }}>
                        📊 분석이 시작된 회원
                    </div>
                    <div
                        className="flex items-baseline"
                        style={{ gap: 4, fontSize: 26, fontWeight: 800, color: '#7BE3B6', marginTop: 4 }}
                    >
                        {membersAnalyzing.count}
                        <span style={{ fontSize: 14, color: FAINT, fontWeight: 700 }}>
                            / {membersAnalyzing.total}명
                        </span>
                    </div>
                    <div style={{ fontSize: 11.5, color: FAINT, marginTop: 3, wordBreak: 'keep-all' }}>
                        수업 스케줄이 잡힌 회원부터 자동 시작
                    </div>
                </div>
            </div>
        </div>

        {/*
          요약 줄 — 카드 집계의 합이다.
          못 잰 항목은 `summaryLine` 이 아예 빼고 적는다. 아는 것만 더해
          숫자를 만들면 카드와 안 맞는 수가 생긴다.
        */}
        <div
            style={{
                borderTop: '1px solid #33415F',
                marginTop: 20,
                padding: '11px 0',
                fontSize: 12.5,
                fontWeight: 600,
                color: DIM,
                wordBreak: 'keep-all',
            }}
        >
            {summaryLine(summary)}
        </div>
    </div>
);

export default OnboardingHero;
